import React from "react";
import {
  Typography,
  Paper,
  Box,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Stack,
  Breadcrumbs,
  Grid,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { LoginButton } from "@/layout/AppLayout";
import { Link, useParams } from "react-router-dom";

// Reusable component for each use case
type UseCasePoint = {
  primary: string;
  secondary?: string;
};

type UseCaseCardProps = {
  title: string;
  description: string;
  videoSrc: string;
  points?: UseCasePoint[];
  previewImage?: string;
};

const UseCaseCard: React.FC<UseCaseCardProps> = ({
  title,
  description,
  videoSrc,
  points = [],
  previewImage,
}) => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    <CardContent sx={{ flexGrow: 1 }}>
      <Typography
        variant="h6"
        component="h3"
        minHeight={{ xs: "1%", sm: "8%" }}
        gutterBottom
      >
        {title}
      </Typography>
      <CardMedia
        component="video"
        src={videoSrc}
        controls
        sx={{
          aspectRatio: "16/9",
          mb: 4,
          height: { xs: "180px", sm: "auto" },
          width: { xs: "97%", md: "100%" },
        }}
        poster={previewImage} // Add a poster image!
      />
      <Typography variant="body2" color="text.secondary" paragraph>
        {description}
      </Typography>
      <List dense>
        {points.map((point, index) => (
          <ListItem key={index} disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircleOutlineIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={point.primary}
              secondary={point.secondary}
              primaryTypographyProps={{ fontWeight: "medium" }}
            />
          </ListItem>
        ))}
      </List>
    </CardContent>
  </Card>
);

export default function UseCaseUI() {
  const { language } = useParams<{ language: string }>();
  const useCasesGerman = {
    title: "Die X2BIM Plattform",
    youtubeLink:
      "https://www.youtube.com/embed/a5lLjfyd5nw?si=D90yymHQV-beLLVN",
    description:
      "Die gemeinsam mit DB Engineering & Consulting entwickelte Plattform X2BIM dient der multidimensionalen Datenerfassung großer Infrastrukturen. Sie bündelt hochwertige Daten aus Multikopterbefliegungen, Mobile-Mapping Systemen, terrestrischem Laserscanning, sowie weiterer Datenquellen.  Punktwolken, Orthophotos und Panoramabilder werden in X2BIM analysiert und ausgewertet, und mittels KI-gestützter Objekterkennung und DSGVO-konformer Anonymisierung veredelt. Hochauflösende 3D-Punktwolken dienen der Bestandsmodellierung, sowie als Datengrundlage für Digitale Geländemodelle (DGM), BIM Modelle und den Digitalen Infrastrukturzwilling.",
    bottomText:
      "  Setzen Sie jetzt auf automatisierte Datenverarbeitung und KI-basierte Auswertung – für mehr Transparenz, Effizienz und Innovation bei Ihrer Infrastruktur! Sichern Sie sich Ihren Vorsprung mit unserer skalierbaren Lösung für die Bahn der Zukunft.",
    useCases: [
      {
        title: "Multisensorik– Die Zukunft der Geodatennutzung ​",
        description:
          "Erleben Sie die Fusion modernster Technologien für Ihre Infrastrukturprojekte in einer Platform: UAS, Mobile Mapping und Video2BIM-Daten vereinen sich zu einer einzigen, zuverlässigen Quelle für alle Geodaten. Mit der Plattform X2BIM wird klassische Vermessung neu gedacht – multisensorisch, digital und effizient. ​",
        videoSrc:
          "https://www.x2bim.com/cms/api/media/file/2025-10-02T18-40-21_X2Bim_User_Stories_2.mp4",
        previewImage: "/public/assets/preview/muiti-preview.png",
        points: [
          {
            primary: "Single Source of Truth",
            secondary:
              "Alle Geodaten werden durch spezifische Qualitätssicherungen geprüft und zentral bereitgestellt. ​",
          },
          {
            primary: "Multisensorische Unterstützung",
            secondary:
              "Luftbilddaten, Punktwolken und Videodokumentationen bilden Ihre Anlage vollständig und detailreich ab.",
          },
          {
            primary: "Nahtlose BIM-Integration",
            secondary:
              "Verknüpfen Sie Ihre Bestandsdaten direkt mit BIM-Modellen und vergleichen Sie Realität und Planung transparent, direkt am Bildschirm. ​",
          },
        ],
      },
      {
        title: "Baufortschritt  ",
        description:
          "Mit X2BIM die gesamte Baustelle im Blick – digital, präzise, intuitiv.",
        videoSrc:
          "https://www.x2bim.com/cms/api/media/file/2025-10-02T18-42-06_X2Bim_User_Stories_1_720HD.mp4",
        previewImage: "/public/assets/preview/construction-preview.png",
        points: [
          {
            primary: "Vergleich As-Is vs. As-Built",
            secondary:
              "Objektiver Fortschritt auf der Baustelle – entdecken Sie Unterschiede und dokumentieren Sie Veränderungen mithilfe intelligenter Visualisierungen und exakt erfassbarer Daten. ​",
          },
          {
            primary: "Multitemporale Analyse",
            secondary:
              "Vergleichen Sie verschiedene Zeitpunkte und erkennen Sie dynamische Entwicklungen, Risiken und Fortschritte direkt im System.",
          },
          {
            primary: "Flexible Messungen ",
            secondary:
              "Egal ob Distanz, Winkel oder Volumen – führen Sie präzise Messungen direkt im digitalen Modell durch und speichern Sie diese für später. ",
          },
          {
            primary: "Messpunktmanagement ",
            secondary:
              "Verfolgen, dokumentieren und exportieren Sie Messungen über 2D- und 3D-Ansichten hinweg – für konsistente Kontrolle und nachvollziehbare Ergebnisse. ",
          },
          {
            primary: "Zusammenarbeit im Team ",
            secondary:
              "Teilen Sie Messungen und Analysen mit Projektbeteiligten, fügen Sie Annotationen hinzu und sichern Sie so eine effiziente, digitale Kommunikation. ",
          },
        ],
      },

      {
        title: "Intelligente Streckenbefahrung & Lichtraumanalyse​",
        description:
          "​Digitalisierung der Bahninfrastruktur – automatisiert und KI-gestützt: Erleben Sie, wie Punktwolken durch moderne KI-Technologien automatisiert ausgewertet und klassifiziert werden – für mehr Sicherheit, Effizienz und Nachvollziehbarkeit in Planung, Betrieb und Instandhaltung. ​",
        videoSrc:
          "https://www.x2bim.com/cms/api/media/file/2025-10-02T18-38-03_Story3.mp4",
        previewImage: "/public/assets/preview/ai-labeling-preview.png",
        points: [
          {
            primary: "KI-Labeling von Punktwolken",
            secondary:
              "Unsere künstliche Intelligenz erkennt, klassifiziert und markiert relevante Objekte direkt in umfangreichen 3D-Daten – schnell, exakt und skalierbar.​",
          },
          {
            primary: "Modelltraining für individuelle Anforderungen",
            secondary:
              "Mithilfe moderner Deep-Learning-Technologien wird das Erkennen spezifischer bahntechnischer Merkmale optimal auf Ihre Projektanforderungen angepasst. ​",
          },
          {
            primary: "Lichtraumanalyse auf Basis von Mobile Mapping",
            secondary:
              "Unser System liefert eine vollständige, automatisierte Analyse des Lichtraums, damit Hindernisse und Bestandsobjekte zuverlässig erkannt werden – und die Sicherheit Ihrer Strecken gewährleistet ist. ​",
          },
          {
            primary: "Ergebnisse bereitstellen & visualisieren",
            secondary:
              "Die Resultate werden über intuitive Plattformen dargestellt – direkt nutzbar für Planung, Inspektion und Instandhaltung. Interaktive Dashboards ermöglichen gezielte Analysen in 2D und 3D. ​",
          },
        ],
      },
      // Add other use cases here...
    ],
  };

  const useCasesEnglish = {
    youtubeLink:
      "https://www.youtube.com/embed/grVy5DEdv14?si=VSsbvedd55mTlQFR",
    title: "The X2BIM Platform",
    description:
      "The X2BIM platform, developed in collaboration with DB Engineering & Consulting, serves the multidimensional data acquisition of large infrastructures. It consolidates high-quality data from UAS surveys, mobile mapping systems, terrestrial laser scanning, and other data sources.   Point clouds, orthophotos and panoramic images are analyzed and evaluated in X2BIM and refined using AI-supported object recognition and GDPR-compliant anonymisation. High-resolution 3D point clouds are used for as-built modelling and as a data basis for digital terrain models (DTM), BIM models and the digital infrastructure twin. To the X2BIM website​ ",
    bottomText:
      " Rely now on automated data processing and AI-based evaluation – for more transparency,efficiency, and innovation in your infrastructure! Secure your competitive edge with our scalable solution for the railway of the future.",
    useCases: [
      {
        title: "Multisensor Technology – The Future of Geodata Utilization",
        description:
          "Experience the fusion of cutting-edge technologies: UAS, mobile mapping and Video2BIM data merge into a single, reliable source for all geodata.",
        videoSrc:
          "https://www.x2bim.com/cms/api/media/file/2025-10-02T18-40-21_X2Bim_User_Stories_2.mp4",
        previewImage: "/public/assets/preview/muiti-preview.png",
        points: [
          {
            primary: "Single Source of Truth",
            secondary:
              "All geodata is checked through quality assurance and centrally provided.",
          },
          {
            primary: "Multisensory Support",
            secondary:
              "Aerial imagery, point clouds, and video capture your facility completely.",
          },
          {
            primary: "Seamless BIM Integration",
            secondary:
              "Link your existing data directly with BIM models and compare reality with planning.",
          },
        ],
      },
      {
        title: "Construction Progress",
        description:
          "With X2BIM, keep the entire construction site in view – digital, precise, and intuitive.",
        videoSrc:
          " https://www.x2bim.com/cms/api/media/file/2025-10-02T18-42-06_X2Bim_User_Stories_1_720HD.mp4",
        previewImage: "/public/assets/preview/construction-preview.png",
        points: [
          {
            primary: "Comparison As-Is vs. As-Built",
            secondary:
              "Identify differences and document changes using intelligent visualizations.",
          },
          {
            primary: "Multitemporal Analysis",
            secondary:
              "Compare different points in time to detect dynamic developments and risks.",
          },
          {
            primary: "Flexible Measurements",
            secondary:
              "Perform precise measurements directly within the digital model.",
          },
          {
            primary: "Survey Point Management",
            secondary:
              " Track, document, and export measurements across 2D and 3D views – for consistent control and traceable results.​",
          },
          {
            primary: "Team Collaboration",
            secondary:
              " Share measurements and analyses with project stakeholders, add annotations, and ensure efficient, digital communication.​",
          },
        ],
      },

      {
        title: "Intelligent Track Surveying & Clearance Analysis​",
        description:
          "Digitalization of Railway Infrastructure – Automated and AI-Powered: See how point clouds are automatically evaluated and classified using modern AI technologies – for greater safety, efficiency, and traceability in planning, operations, and maintenance.​",
        videoSrc:
          "https://www.x2bim.com/cms/api/media/file/2025-10-02T18-38-03_Story3.mp4",
        previewImage: "/public/assets/preview/ai-labeling-preview.png",
        points: [
          {
            primary: "AI Labeling of Point Clouds",
            secondary:
              "Our artificial intelligence detects, classifies, and labels relevant objects directly within extensive 3D data – fast, accurate, and scalable.​",
          },
          {
            primary: "Model Training for Individual Requirements",
            secondary:
              "Using advanced deep learning technologies, the detection of specific railway features is optimally tailored to your project needs.",
          },
          {
            primary: "Clearance Analysis Based on Mobile Mapping",
            secondary:
              " Our system provides a complete, automated clearance profile analysis, ensuring obstacles and existing structures are reliably detected – maintaining the safety of your routes.​",
          },
          {
            primary: "Deliver & Visualize Results",
            secondary:
              "The results are presented via intuitive platforms – ready for immediate use in planning, inspection, and maintenance. Interactive dashboards enable targeted analyses in both 2D and 3D.​",
          },
        ],
      },
      // Add other use cases here...
    ],
  };

  const useCases = language === "en" ? useCasesEnglish : useCasesGerman;

  return (
    <Box sx={{ p: { xs: 1, sm: 4 }, bgcolor: "grey.100" }}>
      <Container maxWidth="lg">
        <Breadcrumbs
          sx={{ display: { xs: "none", sm: "flex" } }}
          aria-label="breadcrumb"
        >
          <Link style={{ color: "initial" }} to="/">
            home
          </Link>

          <Typography sx={{ color: "text.primary" }}>Use Cases</Typography>
        </Breadcrumbs>
        {/* --- Header Section --- */}
        <Paper elevation={0} sx={{ mb: 2, bgcolor: "transparent" }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {useCases.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: "800px", mx: "auto" }}
          >
            {useCases.description}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              height: { xs: "180px", sm: "315px" },
              width: { xs: "320px", md: "560px" },
              mt: 4,
              mb: 2,
              bgcolor: "transparent",
              textAlign: "center",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src={useCases.youtubeLink}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </Paper>
        </Paper>

        {/* --- Use Cases Grid --- */}
        <Grid container spacing={4}>
          {useCases.useCases.map((useCase) => (
            <Grid key={useCase.title} size={{ xs: 12, md: 4 }}>
              <UseCaseCard {...useCase} />
            </Grid>
          ))}
        </Grid>

        <Paper elevation={0} sx={{ p: 4, mb: 6, bgcolor: "transparent" }}>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ maxWidth: "800px", mx: "auto" }}
          >
            {useCases.bottomText}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
